import React from "react";
import { useStyles } from "./OrdersHistoryCard.styles";
import { OrderCardDeliveryItem, OrderCardProps } from "../types";
import { OrderState } from "../../../pages/OrderDetailsPage/OrderDetailsTypes";
import Currency from "../../../shared/components/currency/Currency";
import { useTranslation } from "react-i18next";
import { getDateInfo } from "../../../shared/dates";
import DeliveryStatusTag from "../../DeliveryStatusTag";
import OverflowImageRow from "../../OverflowImageRow";

const DeliveryItem: React.FC<OrderCardDeliveryItem> = ({
  id,
  state,
  deliveryTime,
  lines,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const isCancelled = state?.toLocaleLowerCase() === OrderState.CANCELLED;

  const dateInfo = getDateInfo(deliveryTime);
  const customFormattedDate = isCancelled
    ? t("account.canceledOn", {
        date: dateInfo?.cancelledOn,
      })
    : dateInfo?.customFormattedDate;

  const imageItems = lines?.map((line) => ({
    id: line.id,
    imageUrl: line.featuredAsset?.preview ?? "",
    quantity: line.quantity,
  }));

  return (
    <div className={classes.OrdersHistoryCardDeliveryItem} key={id}>
      <div className={classes.OrdersHistoryCardDeliveryItemHeader}>
        <div className={classes.OrdersHistoryCardDeliveryItemHeaderDate}>
          <span
            className={
              !isCancelled
                ? classes.OrdersHistoryCardDeliveryItemDay
                : classes.OrdersHistoryCardDeliveryItemDayCancelled
            }
          >
            {customFormattedDate}
          </span>
          {!isCancelled ? (
            <span className={classes.OrdersHistoryCardDeliveryItemDate}>
              {t("account.estimating")}
            </span>
          ) : null}
        </div>
        <DeliveryStatusTag state={state as OrderState} />
      </div>
      <OverflowImageRow items={imageItems} />
      <div className={classes.OrdersHistoryCardDeliveryItemDivider} />
    </div>
  );
};

const OrdersHistoryCard: React.FC<OrderCardProps> = ({
  state,
  deliveries,
  totalPrice,
  updatedAt,
  ...rest
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const isCancelled = state?.toLocaleLowerCase() === OrderState.CANCELLED;
  const hasMultipleDeliveries = deliveries?.length > 1 && !isCancelled;

  const allCancelledLines = isCancelled
    ? deliveries?.flatMap((delivery) => delivery.lines) ?? []
    : [];

  return (
    <div className={classes.OrdersHistoryCardContainer} {...rest}>
      <div className={classes.OrdersHistoryCardItems}>
        {!isCancelled ? (
          deliveries?.map((delivery) => (
            <DeliveryItem
              id={delivery?.id}
              state={delivery?.state as OrderState}
              deliveryTime={delivery?.deliveryTime}
              lines={delivery?.lines}
            />
          ))
        ) : (
          <DeliveryItem
            id="cancelled-order"
            state={OrderState.CANCELLED}
            deliveryTime={updatedAt}
            lines={allCancelledLines}
          />
        )}
      </div>
      <div className={classes.OrdersHistoryCardFooter}>
        <span className={classes.OrdersHistoryCardTotal}>
          <Currency />
          {(totalPrice / 100 || 0)?.toFixed(2)}
        </span>
        {hasMultipleDeliveries ? (
          <span className={classes.OrdersHistoryCardMultipleDeliveries}>
            {t("account.multipleDeliveries")}
          </span>
        ) : null}
      </div>
    </div>
  );
};

export default OrdersHistoryCard;
